import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { usePartnerPageMeta } from "@/components/partner/PartnerWorkspaceShell";
import { supabase } from "../lib/supabase";
import { ArrowLeft, Edit, ExternalLink, Calendar, MapPin, DollarSign, Clock, Users, Award, EyeOff, Eye } from "lucide-react";
import { googleCalendarDeadlineUrl } from "@/lib/partnerGoogleCalendar";

const PartnerViewProgram = () => {
  const { id } = useParams();
  const [program, setProgram] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [togglingActive, setTogglingActive] = useState(false);

  const handleToggleActive = async () => {
    if (!program?.id || togglingActive) return;
    const nextActive = !program.is_active;
    setTogglingActive(true);
    try {
      const { error } = await supabase
        .from("programs")
        .update({ is_active: nextActive })
        .eq("id", program.id);
      if (error) throw error;
      setProgram((prev: any) => (prev ? { ...prev, is_active: nextActive } : prev));
      toast.success(
        nextActive
          ? "Program published — visible on Discover again."
          : "Program closed — hidden from Discover. Existing applicants keep their history."
      );
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Could not update this listing.");
    } finally {
      setTogglingActive(false);
    }
  };

  usePartnerPageMeta({
    title: "View program",
    subtitle: "How this listing appears to students",
  });

  useEffect(() => {
    if (id) {
      loadProgramData(id);
    }
  }, [id]);

  const loadProgramData = async (programId: string) => {
    try {
      const { data, error } = await supabase
        .from("programs")
        .select("*")
        .eq("id", programId)
        .single();

      if (error) throw error;
      setProgram(data);
    } catch (error) {
      console.error("Error loading program:", error);
    } finally {
      setLoading(false);
    }
  };

  const parseList = (value: unknown): string[] => {
    if (value == null) return [];
    let raw: unknown = value;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch {
        return raw.trim() ? [raw.trim()] : [];
      }
    }
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          const obj = item as Record<string, unknown>;
          const label = obj.label ?? obj.name ?? obj.title ?? obj.key;
          return typeof label === "string" ? label.trim() : "";
        }
        return "";
      })
      .filter(Boolean);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 rounded-full border-2 border-skutopia-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">Program not found</p>
      </div>
    );
  }

  const eligibility = parseList(program.eligibility_requirements);
  const benefits = parseList(program.program_benefits);
  const applicationReqs = parseList(program.application_requirements);
  const subjects = parseList(program.subject_areas);

  return (
      <div className="max-w-4xl">
        <div className="mb-4 flex flex-wrap gap-2">
          <Link to="/partners/programs">
            <Button variant="outline" size="sm" className="rounded-xl gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </Link>
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl gap-2"
            disabled={togglingActive}
            onClick={handleToggleActive}
          >
            {program.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {togglingActive ? "Updating…" : program.is_active ? "Close listing" : "Publish"}
          </Button>
          <Link to={`/partners/programs/edit/${program.id}`}>
            <Button size="sm" className="rounded-xl gap-2 bg-skutopia-600 hover:bg-skutopia-700">
              <Edit className="w-4 h-4" />
              Edit
            </Button>
          </Link>
        </div>

        {program.image_url && (
          <div className="mb-6 rounded-xl overflow-hidden">
            <img
              src={program.image_url}
              alt={program.title}
              className="w-full h-64 object-cover"
            />
          </div>
        )}

        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 md:p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {program.title}
              </h1>
              <div className="flex flex-wrap gap-2">
                {program.is_active ? (
                  <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium">
                    Published
                  </span>
                ) : (
                  <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-sm font-medium">
                    Closed
                  </span>
                )}
                <span className="px-3 py-1 rounded-full bg-skutopia-100 text-skutopia-700 text-sm font-medium capitalize">
                  {program.category}
                </span>
                <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium capitalize">
                  {program.funding_type}
                </span>
              </div>
            </div>
          </div>

          <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
            {program.description}
          </p>

          {program.long_description && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                About this Program
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                {program.long_description}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <MapPin className="w-5 h-5 text-skutopia-600" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Location</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{program.location || "Global"}</p>
              </div>
            </div>

            {program.application_deadline && (
              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <Calendar className="w-5 h-5 text-skutopia-600" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Application Deadline</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {new Date(program.application_deadline).toLocaleDateString()}
                  </p>
                  <a
                    href={googleCalendarDeadlineUrl({
                      title: program.title,
                      deadline: program.application_deadline,
                      details: program.description || undefined,
                      location: program.location || undefined,
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-skutopia-600 hover:text-skutopia-700"
                  >
                    Add to Google Calendar
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            )}

            {program.duration_days && (
              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <Clock className="w-5 h-5 text-skutopia-600" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Duration</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {program.duration_days} {program.duration_type}
                  </p>
                </div>
              </div>
            )}

            {program.cost && (
              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <DollarSign className="w-5 h-5 text-skutopia-600" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Cost</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">${program.cost}</p>
                </div>
              </div>
            )}

            {program.award_amount_usd && (
              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <Award className="w-5 h-5 text-skutopia-600" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Award Amount</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">${program.award_amount_usd}</p>
                </div>
              </div>
            )}

            {program.program_format && (
              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <Users className="w-5 h-5 text-skutopia-600" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Format</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{program.program_format}</p>
                </div>
              </div>
            )}
          </div>

          {eligibility.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Eligibility Requirements
              </h2>
              <ul className="space-y-2">
                {eligibility.map((req: string, index: number) => (
                  <li key={index} className="flex items-start gap-2 text-gray-600 dark:text-gray-300">
                    <span className="text-skutopia-600 mt-1">•</span>
                    {req}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {benefits.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Program Benefits
              </h2>
              <ul className="space-y-2">
                {benefits.map((benefit: string, index: number) => (
                  <li key={index} className="flex items-start gap-2 text-gray-600 dark:text-gray-300">
                    <span className="text-emerald-600 mt-1">✓</span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {applicationReqs.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Application Requirements
              </h2>
              <ul className="space-y-2">
                {applicationReqs.map((req: string, index: number) => (
                  <li key={index} className="flex items-start gap-2 text-gray-600 dark:text-gray-300">
                    <span className="text-blue-600 mt-1">•</span>
                    {req}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {subjects.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Subjects or courses
              </h2>
              <div className="flex flex-wrap gap-2">
                {subjects.map((subject: string, index: number) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-skutopia-50 text-skutopia-800 border border-skutopia-100 rounded-full text-sm font-medium"
                  >
                    {subject}
                  </span>
                ))}
              </div>
            </div>
          )}

          {program.faculty_info && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Faculty
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                {program.faculty_info}
              </p>
            </div>
          )}

          {program.brochure_url && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Brochure
              </h2>
              <a
                href={program.brochure_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-skutopia-600 hover:text-skutopia-700 underline"
              >
                View brochure
              </a>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {program.grade_level_min && program.grade_level_max && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Grade Level</p>
                <p className="text-gray-900 dark:text-white font-medium">
                  {program.grade_level_min} - {program.grade_level_max}
                </p>
              </div>
            )}

            {program.selectivity_tier && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Selectivity</p>
                <p className="text-gray-900 dark:text-white font-medium capitalize">
                  {program.selectivity_tier}
                </p>
              </div>
            )}

            {program.citizenship_req && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Citizenship</p>
                <p className="text-gray-900 dark:text-white font-medium">
                  {program.citizenship_req}
                </p>
              </div>
            )}

            {program.program_type && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Program Type</p>
                <p className="text-gray-900 dark:text-white font-medium">
                  {program.program_type}
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            {program.application_url && (
              <a
                href={program.application_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-skutopia-600 text-white rounded-lg hover:bg-skutopia-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Apply Now
              </a>
            )}
            {program.external_url && (
              <a
                href={program.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Visit Website
              </a>
            )}
          </div>
        </div>
      </div>
  );
};

export default PartnerViewProgram;
